import { t } from '@lingui/core/macro';
import type { JSX } from 'react';
import { FieldPathValue, FieldValues, Path, PathValue } from 'react-hook-form';

import { FieldController, FieldControllerProps } from '../FieldController';
import { FieldSelect, FieldSelectProps } from './FieldSelect';

export interface FieldSelectControlledProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>,
    TValue extends FieldPathValue<TFieldValues, TName> = PathValue<TFieldValues, TName>,
> extends Omit<FieldControllerProps<FieldSelectProps<TValue>, TFieldValues, TName>, 'Field'> {
    required?: boolean;
}

export function FieldSelectControlled<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>,
    TValue extends FieldPathValue<TFieldValues, TName> = PathValue<TFieldValues, TName>,
>({ required, validate, ...props }: FieldSelectControlledProps<TFieldValues, TName, TValue>): JSX.Element {
    return (
        <FieldController Field={FieldSelect<TValue>} validate={createValidator({ required, validate })} {...props} />
    );
}

function createValidator({
    required,
    validate,
}: {
    required?: boolean;
    validate?: (x: unknown) => string | undefined;
}) {
    return (x: unknown): string | undefined => {
        if (required && !Boolean(x)) {
            return t`Required`;
        }
        if (validate) {
            return validate(x);
        }
        return undefined;
    };
}
